import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import AvailableCreditsTable from "../../../../app-specific/available-credits-table";

const dataGridSaveConfig = {
    name: 'PayMultiInvoicesDialogCreditsSectionDataGrid',
    version: 1,
}

const PayMultiInvoicesDialogCreditsSection = ({
                                                  selectedCredits,
                                                  customerId,
                                                  onAdd,
                                                  onRemove,
                                              }) => {
    const [credits, setCredits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selecting, setSelecting] = useState(undefined);
    const isMounted = useIsMounted();

    const selectedCreditTransactionNos = useMemo(() =>
            selectedCredits?.map(e => e.transactionNo) ?? [],
        [selectedCredits])

    const allCredits = useMemo(() => {
        const all = [...(selectedCredits ?? []), ...(credits ?? [])]
        return Array.from(new Set(all.map(e => e.transactionNo)).values()).map(transactionNo => all.find(e => e.transactionNo === transactionNo));
    }, [selectedCredits, credits])

    /**
     * Loads the list of all available Credits for a specific customer and if the result of the api is successful,
     * sets the list
     */
    const getAllAvailableCredits = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllAvailableCreditByCustomer(customerId)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCredits(response?.data ?? []);
        }
        setLoading(false);
    }, [customerId, isMounted])

    /**
     * As soon as the component mounts:
     * - fetches all the available credits of the provided customerId.
     */
    useEffect(() => {
        getAllAvailableCredits().then();
    }, [customerId, getAllAvailableCredits])

    /**
     * Calls the [onRemove] or [onAdd] callback based on whether this credit has been previously selected.
     *
     * - if the resultFlag of the action is successful, updates the list of credits to retain the credit in the
     * data-grid
     * @param credit
     * @param isSelected
     * @return {Promise<void>}
     */
    const onCreditSelected = async (credit, isSelected) => {
        setSelecting(credit.id)
        let resultFlag;
        if (isSelected) {
            resultFlag = await onRemove(credit);
        } else {
            resultFlag = await onAdd(credit);
        }
        if (!isMounted())
            return;
        setSelecting(undefined);
        if (resultFlag) {
            if (isSelected) {
                setCredits(prevState => [credit, ...prevState])
            } else {
                setCredits(prevState => prevState?.filter(e => e.transactionNo !== credit.transactionNo))
            }
        }
    }

    return (
        <div className='w-100'>
            <div className={'w-100 d-flex px-4'}>
                <p className={'text-sm text-color-2'}>
                    You may use any of the following credits for your selected invoices.
                </p>
            </div>
            <AvailableCreditsTable
                onSelect={onCreditSelected}
                credits={allCredits}
                loading={loading}
                selectingCreditId={selecting}
                selectedCreditTransactionNos={selectedCreditTransactionNos ?? []}
                saveConfig={dataGridSaveConfig}
                visibleRows={4}
            />
        </div>
    );
}

export default PayMultiInvoicesDialogCreditsSection;
